/**
 * Page /urbanisme — back-office instruction des demandes d'urbanisme (staff).
 * Lot 2 : jauge de délai légal, demande de pièces complémentaires (suspension),
 * avis des services consultés. Trois onglets via la prop `tab`. UrbanismeLayout,
 * zéro window.confirm.
 */

import { useEffect, useState, useCallback } from 'react';
import { UrbanismeLayout } from '@/components/UrbanismeLayout';
import { UrbanismeNavKey } from '@/components/UrbanismeNav';
import { ParcelleMap } from '@/components/urbanisme/ParcelleMap';
import { Card, Button, Badge, Alert, SectionEyebrow, Input } from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException } from '@/lib/api';
import type { UrbaDemande, UrbaStatut, UrbaAvis, UrbaAvisSens } from '@/lib/api';
import { FileText, AlertCircle, Download, CheckCircle2, XCircle, Trash2, ClipboardCheck, Clock, Plus, FileSignature, ShieldCheck, RefreshCw, MapPin, ExternalLink, Landmark, Info as InfoIcon, Mail } from 'lucide-react';

const TYPE_LABEL: Record<string, string> = { CU: 'Certificat d\'urbanisme', DP: 'Déclaration préalable', PC: 'Permis de construire', PA: 'Permis d\'aménager' };
const SERVICES_PREDEF = ['ABF — Architecte des Bâtiments de France', 'Gestionnaire de voirie', 'SDIS — service incendie', 'ARS — santé', 'Gestionnaire de réseaux', 'EPCI / intercommunalité'];
const SENS_LABEL: Record<UrbaAvisSens, string> = { favorable: 'Favorable', defavorable: 'Défavorable', favorable_reserves: 'Favorable avec réserves', en_attente: 'En attente' };
function sensVariant(s: UrbaAvisSens): 'success' | 'urgent' | 'warning' | 'neutral' {
  return s === 'favorable' ? 'success' : s === 'defavorable' ? 'urgent' : s === 'favorable_reserves' ? 'warning' : 'neutral';
}

function statutBadge(s: UrbaStatut): { label: string; variant: 'success' | 'warning' | 'neutral' | 'urba' | 'urgent' } {
  switch (s) {
    case 'deposee': return { label: 'Déposée', variant: 'neutral' };
    case 'complete': return { label: 'Complète', variant: 'urba' };
    case 'incomplete': return { label: 'Incomplète', variant: 'warning' };
    case 'instruction': return { label: 'En instruction', variant: 'warning' };
    case 'accordee': return { label: 'Accordée', variant: 'success' };
    case 'refusee': return { label: 'Refusée', variant: 'urgent' };
    case 'annulee': return { label: 'Annulée', variant: 'neutral' };
  }
}
function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/Paris' });
}
function fmtDateOnly(d: string): string {
  return new Date(`${d}T00:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function UrbanismeDossiersPage({ tab }: { tab: UrbanismeNavKey }) {
  const { confirm, prompt, dialogElement } = useConfirmDialog();
  const [items, setItems] = useState<UrbaDemande[]>([]);
  const [selected, setSelected] = useState<UrbaDemande | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [avisOpen, setAvisOpen] = useState(false);
  const [signDispo, setSignDispo] = useState(false);
  const [actesGuide, setActesGuide] = useState<{ tiersTeletransmission: string | null; lienPrefectureActes: string | null }>({ tiersTeletransmission: null, lienPrefectureActes: null });

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const filtre = tab === 'instruire' ? { aInstruire: true } : tab === 'decides' ? { decides: true } : undefined;
      const res = await api.urbanisme.listDemandes(filtre);
      setItems(res.demandes);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Chargement impossible'); }
    finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { void reload(); setSelected(null); }, [reload]);
  useEffect(() => { void api.urbanisme.config().then((c) => { setSignDispo(c.signatureDisponible); setActesGuide(c.actes); }).catch(() => {}); }, []);

  async function marquerTransmis(d: UrbaDemande) {
    try { const up = await api.urbanisme.marquerActesTransmis(d.id); setSelected(await api.urbanisme.getDemande(up.id)); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function annulerTransmis(d: UrbaDemande) {
    try { const up = await api.urbanisme.annulerActesTransmis(d.id); setSelected(await api.urbanisme.getDemande(up.id)); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  // Actions arrêté & signature
  async function genererArrete(d: UrbaDemande) {
    try { const up = await api.urbanisme.genererArrete(d.id); setSelected(await api.urbanisme.getDemande(up.id)); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function telechargerArrete(d: UrbaDemande) {
    try { const { url } = await api.urbanisme.arreteUrl(d.id); window.open(url, '_blank', 'noopener'); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function envoyerSignature(d: UrbaDemande) {
    try { const up = await api.urbanisme.envoyerSignature(d.id); setSelected(await api.urbanisme.getDemande(up.id)); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function verifierSignature(d: UrbaDemande) {
    try { const up = await api.urbanisme.statutSignature(d.id); setSelected(await api.urbanisme.getDemande(up.id)); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function rafraichirCadastre(d: UrbaDemande) {
    try { const up = await api.urbanisme.rafraichirCadastre(d.id); setSelected(await api.urbanisme.getDemande(up.id)); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function ouvrirGeoportail(d: UrbaDemande) {
    try { const { url } = await api.urbanisme.geoportailUrl(d.id); window.open(url, '_blank', 'noopener'); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  async function openDetail(d: UrbaDemande) {
    try { setSelected(await api.urbanisme.getDemande(d.id)); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function refreshDetail(id: string) {
    try { setSelected(await api.urbanisme.getDemande(id)); } catch { /* */ }
  }

  async function setStatut(d: UrbaDemande, statut: Exclude<UrbaStatut, 'deposee' | 'annulee'>) {
    let body: Parameters<typeof api.urbanisme.changerStatut>[1] = { statut };
    if (statut === 'accordee' || statut === 'refusee') {
      const r = await prompt({ title: statut === 'accordee' ? 'Accorder la demande' : 'Refuser la demande', description: 'Motivation (figurera dans l\'email envoyé au déposant).', confirmLabel: statut === 'accordee' ? 'Accorder' : 'Refuser', placeholder: 'Motif de la décision…', multiline: true });
      if (r === null) return; body = { statut, motif: r || null };
    } else if (statut === 'incomplete') {
      const liste = await prompt({ title: 'Demander des pièces complémentaires', description: 'Listez les pièces manquantes (une par ligne). Le délai sera suspendu et redémarrera à la complétude.', confirmLabel: 'Continuer', placeholder: 'Plan de masse coté (PCMI2)\nPlan en coupe (PCMI3)…', multiline: true, required: true });
      if (liste === null) return;
      const message = await prompt({ title: 'Message au demandeur', description: 'Précisions envoyées par email (optionnel).', confirmLabel: 'Envoyer la demande', placeholder: 'Merci de fournir ces pièces sous 3 mois…', multiline: true });
      body = { statut, piecesDemandees: liste, piecesMessage: message || null };
    } else {
      const ok = await confirm({ title: 'Changer le statut ?', description: statut === 'complete' ? 'Le dossier devient complet : le délai d\'instruction (re)démarre aujourd\'hui.' : 'Le déposant sera notifié.', confirmLabel: 'Confirmer' });
      if (!ok) return;
    }
    try { const updated = await api.urbanisme.changerStatut(d.id, body); setSelected(await api.urbanisme.getDemande(updated.id)); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  async function openPiece(pieceId: string) {
    try { const { url } = await api.urbanisme.pieceUrl(pieceId); window.open(url, '_blank', 'noopener'); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function supprimer(d: UrbaDemande) {
    const ok = await confirm({ title: 'Supprimer ce dossier ?', description: 'Le dossier et ses pièces seront effacés. Action irréversible.', danger: true, confirmLabel: 'Supprimer' });
    if (!ok) return;
    try { await api.urbanisme.removeDemande(d.id); setSelected(null); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function removeAvis(avisId: string) {
    if (!selected) return;
    try { await api.urbanisme.removeAvis(avisId); await refreshDetail(selected.id); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  const title = tab === 'instruire' ? 'Dossiers à instruire' : tab === 'decides' ? 'Dossiers décidés' : 'Dossiers déposés';

  return (
    <UrbanismeLayout active={tab}>
      <main id="main-content" className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8 animate-fade-in">
        <SectionEyebrow as="div">Autorisations d'urbanisme</SectionEyebrow>
        <h1 className="mt-1.5 font-display text-xl font-semibold tracking-r-tight text-ink sm:text-2xl">{title}</h1>
        <p className="mt-2 text-sm text-ink-muted">Instruisez les demandes : délai légal, complétude, avis des services, décision. Chaque changement notifie l'administré.</p>

        {error && <Alert variant="error" icon={AlertCircle} className="mt-4">{error}</Alert>}

        <div className="mt-6 space-y-3">
          {loading ? (
            <Card variant="glass" padded><p className="text-sm text-ink-muted">Chargement…</p></Card>
          ) : items.length === 0 ? (
            <Card variant="glass" padded><p className="text-sm text-ink-muted">Aucun dossier dans cette vue.</p></Card>
          ) : items.map((d) => {
            const sb = statutBadge(d.statut); const isSel = selected?.id === d.id;
            return (
              <Card key={d.id} variant="glass" padded>
                <button type="button" onClick={() => (isSel ? setSelected(null) : openDetail(d))} className="flex w-full items-center gap-3 text-left">
                  <Badge variant="urba" size="sm">{d.typeDemande}{d.typeDemande === 'CU' && d.cuNature === 'operationnel' ? 'b' : d.typeDemande === 'CU' ? 'a' : ''}</Badge>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-ink">{d.description?.slice(0, 70) || TYPE_LABEL[d.typeDemande]}</div>
                    <div className="font-mono text-xs text-ink-dim">{d.numero} · {d.deposantNom ?? '—'} · déposée le {fmtDate(d.createdAt)}</div>
                  </div>
                  {d.joursRestants != null && (d.statut === 'instruction' || d.statut === 'complete') && (
                    <span className={`text-xs font-medium tabular-nums ${d.joursRestants < 0 ? 'text-urgent' : d.joursRestants <= 15 ? 'text-warning' : 'text-success'}`}>
                      {d.joursRestants < 0 ? `+${-d.joursRestants} j` : `${d.joursRestants} j`}
                    </span>
                  )}
                  <Badge variant={sb.variant} size="sm">{sb.label}</Badge>
                </button>

                {isSel && selected && (
                  <div className="mt-4 border-t border-border pt-4">
                    {/* Jauge de délai */}
                    {selected.dateLimite && (selected.statut === 'instruction' || selected.statut === 'complete') && (
                      <DelaiGauge demande={selected} />
                    )}
                    {selected.statut === 'incomplete' && (
                      <div className="mb-3 rounded-r-md border border-warning/30 bg-warning-tint/40 p-3 text-sm text-warning">
                        ⏱️ Délai suspendu — en attente des pièces complémentaires. Il redémarrera quand vous marquerez le dossier « complet ».
                      </div>
                    )}

                    <div className="grid gap-3 sm:grid-cols-2">
                      <Info label="Déposant" value={`${selected.deposantNom ?? '—'}${selected.deposantEmail ? ` · ${selected.deposantEmail}` : ''}`} />
                      <Info label="Parcelle / surface" value={`${selected.parcelle ?? '—'}${selected.surfaceM2 != null ? ` · ${selected.surfaceM2} m²` : ''}`} />
                      <Info label="Adresse du terrain" value={selected.adresseTerrain ?? '—'} />
                      <Info label="Type / délai légal" value={`${TYPE_LABEL[selected.typeDemande] ?? selected.typeDemande}${selected.delaiMois ? ` · ${selected.delaiMois} mois` : ''}`} />
                    </div>
                    {selected.description && <div className="mt-3"><div className="text-2xs uppercase tracking-r-eyebrow text-ink-dim">Description</div><p className="mt-0.5 text-sm text-ink">{selected.description}</p></div>}

                    <CadastreBloc demande={selected} onRafraichir={() => rafraichirCadastre(selected)} onGeoportail={() => ouvrirGeoportail(selected)} />

                    {selected.pieces && selected.pieces.length > 0 && (
                      <div className="mt-3">
                        <div className="text-2xs uppercase tracking-r-eyebrow text-ink-dim mb-1">Pièces déposées</div>
                        <div className="space-y-1.5">
                          {selected.pieces.map((p) => (
                            <button key={p.id} type="button" onClick={() => openPiece(p.id)} className="flex w-full items-center gap-2 rounded-r-md border border-border bg-surface/50 px-3 py-2 text-left text-sm hover:border-border-strong">
                              <FileText className="h-4 w-4 flex-none text-urba-fg" />
                              <span className="min-w-0 flex-1 truncate text-ink">{p.nomFichier ?? 'pièce'}</span>
                              <Download className="h-4 w-4 flex-none text-ink-dim" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Avis des services */}
                    <div className="mt-4">
                      <div className="flex items-center justify-between">
                        <div className="text-2xs uppercase tracking-r-eyebrow text-ink-dim">Avis des services consultés</div>
                        <Button variant="ghost" size="sm" onClick={() => setAvisOpen(true)}><Plus className="h-4 w-4" /> Solliciter un avis</Button>
                      </div>
                      {selected.avis && selected.avis.length > 0 ? (
                        <div className="mt-1 divide-y divide-border">
                          {selected.avis.map((a) => (
                            <div key={a.id} className="flex items-start gap-2 py-2">
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-ink">{a.service}</div>
                                <div className="text-2xs text-ink-dim">{a.saisiAt ? `saisi le ${fmtDateOnly(a.saisiAt)}` : ''}{a.reponseAt ? ` · réponse le ${fmtDateOnly(a.reponseAt)}` : ''}</div>
                                {a.commentaire && <div className="text-xs text-ink-muted mt-0.5">{a.commentaire}</div>}
                              </div>
                              <Badge variant={sensVariant(a.sens)} size="sm">{SENS_LABEL[a.sens]}</Badge>
                              <button type="button" onClick={() => removeAvis(a.id)} aria-label="Retirer" className="text-ink-dim hover:text-urgent"><Trash2 className="h-3.5 w-3.5" /></button>
                            </div>
                          ))}
                        </div>
                      ) : <p className="mt-1 text-sm text-ink-dim">Aucun avis sollicité.</p>}
                    </div>

                    {selected.motifDecision && (
                      <div className="mt-3 rounded-r-md border border-border bg-bg/50 p-2.5 text-sm text-ink-muted"><span className="font-medium text-ink">Motif :</span> {selected.motifDecision}</div>
                    )}

                    {(selected.statut === 'accordee' || selected.statut === 'refusee') && (
                      <ArreteBloc
                        demande={selected}
                        signDispo={signDispo}
                        onGenerer={() => genererArrete(selected)}
                        onTelecharger={() => telechargerArrete(selected)}
                        onEnvoyer={() => envoyerSignature(selected)}
                        onVerifier={() => verifierSignature(selected)}
                      />
                    )}

                    {(selected.statut === 'accordee' || selected.statut === 'refusee') && selected.arreteGenereAt && (
                      <ActesBloc
                        demande={selected}
                        guide={actesGuide}
                        onMarquer={() => marquerTransmis(selected)}
                        onAnnuler={() => annulerTransmis(selected)}
                      />
                    )}

                    {selected.statut !== 'annulee' && (
                      <div className="mt-4">
                        <div className="text-2xs uppercase tracking-r-eyebrow text-ink-dim mb-1.5">Changer le statut (notifie l'administré)</div>
                        <div className="flex flex-wrap gap-2">
                          <Button variant="ghost" size="sm" onClick={() => setStatut(selected, 'complete')}><ClipboardCheck className="h-4 w-4" /> Complète</Button>
                          <Button variant="ghost" size="sm" onClick={() => setStatut(selected, 'incomplete')} className="text-warning">Demander des pièces</Button>
                          <Button variant="ghost" size="sm" onClick={() => setStatut(selected, 'instruction')}>En instruction</Button>
                          <Button variant="primary" size="sm" onClick={() => setStatut(selected, 'accordee')}><CheckCircle2 className="h-4 w-4" /> Accorder</Button>
                          <Button variant="ghost" size="sm" onClick={() => setStatut(selected, 'refusee')} className="text-urgent"><XCircle className="h-4 w-4" /> Refuser</Button>
                        </div>
                      </div>
                    )}
                    <div className="mt-3 flex justify-end">
                      <Button variant="ghost" size="sm" onClick={() => supprimer(selected)} className="text-ink-muted hover:text-urgent"><Trash2 className="h-4 w-4" /> Supprimer</Button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </main>

      {avisOpen && selected && (
        <AvisModal onClose={() => setAvisOpen(false)} onSaved={async () => { setAvisOpen(false); await refreshDetail(selected.id); }} demandeId={selected.id} onError={setError} />
      )}
      {dialogElement}
    </UrbanismeLayout>
  );
}

function DelaiGauge({ demande }: { demande: UrbaDemande }) {
  const jr = demande.joursRestants ?? 0;
  const tone = jr < 0 ? 'bad' : jr <= 15 ? 'warn' : 'ok';
  const cls = tone === 'bad' ? 'border-urgent/30 bg-urgent-tint/40' : tone === 'warn' ? 'border-warning/30 bg-warning-tint/40' : 'border-success/30 bg-success-tint/40';
  const big = tone === 'bad' ? 'text-urgent' : tone === 'warn' ? 'text-warning' : 'text-success';
  return (
    <div className={`mb-3 flex items-center gap-4 rounded-r-lg border p-3 ${cls}`}>
      <Clock className={`h-5 w-5 flex-none ${big}`} />
      <div className="flex-1">
        <div className={`font-display text-lg font-semibold ${big}`}>{jr < 0 ? `Dépassé de ${-jr} j` : `${jr} j restants`}</div>
        <div className="text-xs text-ink-muted">Délai légal {demande.delaiMois} mois · date limite {demande.dateLimite ? fmtDateOnly(demande.dateLimite) : '—'}</div>
        <div className="mt-1 text-2xs text-ink-dim">À défaut de décision avant l'échéance : décision tacite (silence vaut accord/refus selon le cas).</div>
      </div>
    </div>
  );
}

function AvisModal({ demandeId, onClose, onSaved, onError }: { demandeId: string; onClose: () => void; onSaved: () => void; onError: (m: string) => void }) {
  const [service, setService] = useState('');
  const [sens, setSens] = useState<UrbaAvisSens>('en_attente');
  const [commentaire, setCommentaire] = useState('');
  const [saisiAt, setSaisiAt] = useState('');
  const [busy, setBusy] = useState(false);

  async function save() {
    if (service.trim().length < 1) { onError('Service requis'); return; }
    setBusy(true);
    try {
      await api.urbanisme.addAvis(demandeId, { service: service.trim(), sens, commentaire: commentaire.trim() || null, saisiAt: saisiAt || null });
      onSaved();
    } catch (e) { onError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-r-xl border border-border bg-surface p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-base font-semibold text-ink mb-3">Solliciter / enregistrer un avis</h3>
        <label className="block text-sm font-medium text-ink mb-1">Service consulté</label>
        <Input value={service} onChange={(e) => setService(e.target.value)} placeholder="ABF, voirie, SDIS…" list="services-urba" />
        <datalist id="services-urba">{SERVICES_PREDEF.map((s) => <option key={s} value={s} />)}</datalist>
        <label className="mt-3 block text-sm font-medium text-ink mb-1">Sens de l'avis</label>
        <select value={sens} onChange={(e) => setSens(e.target.value as UrbaAvisSens)} className="h-10 w-full rounded-r-lg border border-border bg-surface/60 px-3 text-sm">
          {(['en_attente', 'favorable', 'favorable_reserves', 'defavorable'] as UrbaAvisSens[]).map((s) => <option key={s} value={s}>{SENS_LABEL[s]}</option>)}
        </select>
        <label className="mt-3 block text-sm font-medium text-ink mb-1">Date de saisine (optionnel)</label>
        <input type="date" value={saisiAt} onChange={(e) => setSaisiAt(e.target.value)} className="h-10 w-full rounded-r-lg border border-border bg-surface/60 px-3 text-sm" />
        <label className="mt-3 block text-sm font-medium text-ink mb-1">Commentaire (optionnel)</label>
        <textarea value={commentaire} onChange={(e) => setCommentaire(e.target.value)} rows={2} className="w-full rounded-r-lg border border-border bg-surface/60 px-3 py-2 text-sm" />
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button variant="primary" onClick={save} disabled={busy}>{busy ? 'Enregistrement…' : 'Enregistrer'}</Button>
        </div>
      </div>
    </div>
  );
}

function ActesBloc({ demande, guide, onMarquer, onAnnuler }: {
  demande: UrbaDemande;
  guide: { tiersTeletransmission: string | null; lienPrefectureActes: string | null };
  onMarquer: () => void; onAnnuler: () => void;
}) {
  const transmis = !!demande.actesTransmisAt;
  const tiers = guide.tiersTeletransmission;
  return (
    <div className="mt-3 rounded-r-lg border border-tech/50 overflow-hidden">
      <div className="flex items-center gap-2 bg-tech-tint px-3.5 py-2.5 border-b border-border">
        <Landmark className="h-4 w-4 text-tech-fg" />
        <span className="text-sm font-semibold text-tech-fg">Transmission au contrôle de légalité</span>
        {transmis
          ? <Badge variant="success" size="sm" className="ml-auto">Transmis</Badge>
          : <Badge variant="warning" size="sm" className="ml-auto">À transmettre</Badge>}
      </div>
      <div className="p-3.5">
        {transmis ? (
          <>
            <div className="flex items-start gap-2.5 rounded-r-md border border-success/40 bg-success-tint px-3 py-2.5 text-sm text-success-fg">
              <CheckCircle2 className="h-5 w-5 flex-none" />
              <div>Transmis au contrôle de légalité le <b>{fmtDate(demande.actesTransmisAt!)}</b>{tiers ? <> — via {tiers}</> : null}.</div>
            </div>
            <div className="mt-2.5"><Button variant="ghost" size="sm" onClick={onAnnuler}><RefreshCw className="h-4 w-4" /> Annuler / corriger la date</Button></div>
          </>
        ) : (
          <>
            <ol className="space-y-2 text-sm text-ink-muted">
              <li>1. Télécharger l'arrêté signé (bloc ci-dessus).</li>
              <li>2. Ouvrir votre outil de télétransmission{tiers ? <> <b className="text-ink">{tiers}</b></> : ' habituel'}.</li>
              <li>3. Déposer l'arrêté sous la nature <b className="text-ink">« Autorisation d'urbanisme »</b> (code émetteur commune <code className="text-xs bg-bg px-1 rounded">3-1</code>).</li>
              <li>4. Après accusé de réception de la préfecture, revenir cocher ci-dessous.</li>
            </ol>
            <div className="mt-3 flex items-start gap-2 rounded-r-md bg-amber-tint/60 border border-amber/40 px-3 py-2 text-xs text-amber-fg">
              <InfoIcon className="h-4 w-4 flex-none" />
              <span>VPC ne transmet pas directement à @ACTES (système fermé, accès via opérateur homologué). VPC produit l'arrêté et trace la transmission.</span>
            </div>
            <div className="mt-2 flex flex-col gap-1">
              <a href="https://www.collectivites-locales.gouv.fr/connaitre-les-acteurs-et-les-institutions/controle-de-legalite/actes-la-dematerialisation-de-la-transmission-des-actes" target="_blank" rel="noopener noreferrer" className="text-xs text-tech-fg inline-flex items-center gap-1.5"><ExternalLink className="h-3.5 w-3.5" /> Portail @CTES — collectivites-locales.gouv.fr</a>
              {guide.lienPrefectureActes && <a href={guide.lienPrefectureActes} target="_blank" rel="noopener noreferrer" className="text-xs text-tech-fg inline-flex items-center gap-1.5"><ExternalLink className="h-3.5 w-3.5" /> Notre préfecture — télétransmission des actes</a>}
            </div>
            <div className="mt-3"><Button variant="primary" size="sm" onClick={onMarquer}><CheckCircle2 className="h-4 w-4" /> Marquer comme transmis</Button></div>
          </>
        )}
      </div>
    </div>
  );
}

function CadastreBloc({ demande, onRafraichir, onGeoportail }: { demande: UrbaDemande; onRafraichir: () => void; onGeoportail: () => void }) {
  const aData = !!demande.parcelleGeojson || demande.parcelleSurfaceCad != null || !!demande.zonagePlu;
  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-2xs uppercase tracking-r-eyebrow text-ink-dim flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Localisation cadastrale</div>
        <div className="flex gap-1.5">
          <Button variant="ghost" size="sm" onClick={onRafraichir}><RefreshCw className="h-4 w-4" /> Rafraîchir depuis le cadastre</Button>
          {aData && <Button variant="ghost" size="sm" onClick={onGeoportail}><ExternalLink className="h-4 w-4" /> Géoportail</Button>}
        </div>
      </div>
      {demande.parcelleGeojson || demande.parcelleCentroide ? (
        <ParcelleMap geojson={demande.parcelleGeojson} centroide={demande.parcelleCentroide} height={240} />
      ) : (
        <div className="flex h-28 items-center justify-center rounded-r-md border border-border bg-bg/40 text-center text-sm text-ink-dim">
          Aucune parcelle localisée.<br />Renseignez une référence puis « Rafraîchir depuis le cadastre ».
        </div>
      )}
      {aData && (
        <div className="mt-2 grid gap-x-4 gap-y-1 sm:grid-cols-2">
          {(demande.parcelleSection || demande.parcelleNumero) && <KV k="Parcelle" v={`${demande.parcelleSection ?? ''} ${demande.parcelleNumero ?? ''}`.trim()} />}
          {demande.parcelleSurfaceCad != null && <KV k="Contenance cadastrale" v={`${demande.parcelleSurfaceCad} m²`} />}
          {demande.zonagePlu && <KV k="Zonage PLU" v={demande.zonagePlu} badge />}
          {demande.cadastreMajAt && <KV k="Mise à jour" v={fmtDate(demande.cadastreMajAt)} />}
        </div>
      )}
    </div>
  );
}

function KV({ k, v, badge }: { k: string; v: string; badge?: boolean }) {
  return (
    <div className="flex items-center justify-between border-b border-border py-1.5 text-sm last:border-0">
      <span className="text-ink-muted">{k}</span>
      {badge ? <Badge variant="tech" size="sm">{v}</Badge> : <span className="font-medium text-ink">{v}</span>}
    </div>
  );
}

function ArreteBloc({ demande, signDispo, onGenerer, onTelecharger, onEnvoyer, onVerifier }: {
  demande: UrbaDemande; signDispo: boolean;
  onGenerer: () => void; onTelecharger: () => void; onEnvoyer: () => void; onVerifier: () => void;
}) {
  const genere = !!demande.arreteGenereAt;
  const sig = demande.signatureStatut;
  const signe = sig === 'signe';
  return (
    <div className="mt-4 space-y-3">
      {/* Arrêté */}
      <div className="rounded-r-lg border border-border p-3">
        <div className="flex items-center gap-2">
          <FileSignature className="h-4 w-4 text-urba-fg" />
          <span className="text-sm font-semibold text-ink">Arrêté municipal</span>
          {genere && <Badge variant={signe ? 'success' : 'urba'} size="sm">{signe ? 'Signé' : 'Généré'}</Badge>}
        </div>
        <p className="mt-1 text-xs text-ink-muted">
          {genere
            ? `Arrêté n° ${demande.numero}${demande.arreteGenereAt ? ` · généré le ${fmtDate(demande.arreteGenereAt)}` : ''}. Reprend les réserves éventuelles des avis.`
            : 'Générez l\'arrêté PDF de décision (entête République Française, visas, articles).'}
        </p>
        <div className="mt-2.5 flex flex-wrap gap-2">
          {!genere ? (
            <Button variant="primary" size="sm" onClick={onGenerer}><FileSignature className="h-4 w-4" /> Générer l'arrêté</Button>
          ) : (
            <>
              <Button variant="primary" size="sm" onClick={onTelecharger}><Download className="h-4 w-4" /> Télécharger</Button>
              {!signe && <Button variant="ghost" size="sm" onClick={onGenerer}><RefreshCw className="h-4 w-4" /> Régénérer</Button>}
            </>
          )}
        </div>
      </div>

      {/* Signature électronique */}
      {genere && (
        <div className="rounded-r-lg border border-border bg-bg/40 p-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-tech-fg" />
            <span className="text-sm font-semibold text-ink">Signature électronique</span>
            <Badge variant="tech" size="sm">eIDAS · Yousign</Badge>
            {sig === 'en_cours' && <Badge variant="warning" size="sm">En cours</Badge>}
            {signe && <Badge variant="success" size="sm">Signé</Badge>}
          </div>
          {!signDispo ? (
            <>
              <p className="mt-1 text-xs text-ink-muted">La signature électronique n'est pas encore activée (habilitation Yousign en attente). L'arrêté est signé manuellement par le maire.</p>
              <Button variant="ghost" size="sm" disabled className="mt-2 opacity-60"><FileSignature className="h-4 w-4" /> Envoyer à la signature (indisponible)</Button>
            </>
          ) : signe ? (
            <p className="mt-1 text-xs text-success">Arrêté signé électroniquement (eIDAS).</p>
          ) : sig === 'en_cours' ? (
            <>
              <div className="mt-1 rounded-r-md border border-warning/40 bg-warning-tint/30 p-2.5">
                <p className="text-xs font-medium text-ink">Un email de signature a été envoyé au maire.</p>
                <p className="mt-1 text-xs text-ink-muted">
                  Ouvrez votre boîte mail, cliquez sur le lien « Signer » de Yousign, puis signez.
                  Revenez ensuite ici et cliquez sur « Vérifier le statut » : l'arrêté passera à « Signé ».
                </p>
                <a
                  href="https://mail.google.com/mail/u/0/#search/yousign"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-grc hover:underline"
                >
                  <Mail className="h-3.5 w-3.5" aria-hidden="true" /> Ouvrir ma boîte Gmail
                </a>
              </div>
              <Button variant="ghost" size="sm" onClick={onVerifier} className="mt-2"><RefreshCw className="h-4 w-4" /> Vérifier le statut</Button>
            </>
          ) : (
            <>
              <p className="mt-1 text-xs text-ink-muted">Envoyez l'arrêté à la signature électronique qualifiée du maire.</p>
              <Button variant="primary" size="sm" onClick={onEnvoyer} className="mt-2"><FileSignature className="h-4 w-4" /> Envoyer à la signature</Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-2xs uppercase tracking-r-eyebrow text-ink-dim">{label}</div>
      <div className="mt-0.5 text-sm text-ink">{value}</div>
    </div>
  );
}
